import React, {
    useEffect,
    useRef,
    useContext,
    useCallback,
    useReducer,
    useState,
    ReactElement,
} from 'react';
import { FormikProps } from 'formik';
import {
    Group,
    Button,
    useNotification,
    Message,
    Text,
    Box,
    Divider,
    Heading,
    Alert,
    Flex,
    Checkbox,
} from '@landr/maestro';
import { Trans, t } from '@lingui/macro';
import {
    Input,
    TextArea,
    LabelField,
    GenreSelect,
    SubgenreSelect,
    ExclusivityRadio,
    LexiconRadio,
    FormFieldComponentProps,
} from 'components/Form';
import { getUppyInstance } from 'utils/multipartUpload';
import { i18n } from 'utils/setupTranslations';
import { log } from 'utils/log';
import { AuthenticationContext } from 'contexts/AuthenticationContext';
import { useNewPackFormSchema } from 'hooks/useNewPackFormSchema';
import { FormFields } from 'types';
import { EventIds } from 'types/enums';
import { checkIsUploadField } from '../NewPack.utils';
import {
    newPackFieldTitles,
    UPLOAD_ENPOINTS,
    UploadKey,
    UploadStatus,
    FieldProgress,
    FieldProgressType,
    defaultFieldProgress,
    newPackFieldOrderPart1,
    newPackFieldOrderPart2,
    newPackFieldOrderPart3,
    newPackFieldOrderPart4,
    newPackFieldOrderPart5,
} from '../NewPack.constants';
import {
    NewPackFormKeys,
    NewPackFormFields,
    NewPackFormVisibleFieldKeys,
} from '../NewPack.types';
import { UploadFieldContext } from './UploadFieldContext';
import { UploadMessageWrapper } from './NewPackForm.styled';
import {
    PackUpload,
    ImageUpload,
    DemoUpload,
    CsvUpload,
} from './NewPackForm.fields';
import { InputLayout } from './InputLayout';

export const initialNewPackFormValues: NewPackFormFields = {
    [NewPackFormKeys.Name]: '',
    [NewPackFormKeys.Description]: '',
    [NewPackFormKeys.LabelName]: '',
    [NewPackFormKeys.Genre]: '',
    [NewPackFormKeys.Subgenre]: '',
    [NewPackFormKeys.Exclusivity]: '',
    [NewPackFormKeys.Lexicon]: '',
    [NewPackFormKeys.CsvUpload]: undefined,
    [NewPackFormKeys.DemoUpload]: undefined,
    [NewPackFormKeys.ArtworkUpload]: undefined,
    [NewPackFormKeys.ZipUpload]: undefined,
    [NewPackFormKeys.CsvUploadId]: '',
    [NewPackFormKeys.DemoUploadId]: '',
    [NewPackFormKeys.ArtworkUploadId]: '',
    [NewPackFormKeys.ZipUploadId]: '',
};

export const fieldComponents: Record<
    NewPackFormVisibleFieldKeys,
    React.FC<FormFieldComponentProps<FormFields | NewPackFormFields>>
> = {
    [NewPackFormKeys.Name]: Input,
    [NewPackFormKeys.Description]: TextArea,
    [NewPackFormKeys.LabelName]: LabelField,
    [NewPackFormKeys.Genre]: GenreSelect,
    [NewPackFormKeys.Subgenre]: SubgenreSelect,
    [NewPackFormKeys.Exclusivity]: ExclusivityRadio,
    [NewPackFormKeys.Lexicon]: LexiconRadio,
    [NewPackFormKeys.CsvUpload]: CsvUpload,
    [NewPackFormKeys.DemoUpload]: DemoUpload,
    [NewPackFormKeys.ArtworkUpload]: ImageUpload,
    [NewPackFormKeys.ZipUpload]: PackUpload,
};

type FieldStatusType = {
    csvUpload: UploadStatus;
    demoUpload: UploadStatus;
    artworkUpload: UploadStatus;
    zipUpload: UploadStatus;
};

const defaultFieldStatuses: FieldStatusType = {
    csvUpload: 'pending',
    demoUpload: 'pending',
    artworkUpload: 'pending',
    zipUpload: 'pending',
};

export type FieldStatusAction =
    | {
          type: NewPackFormKeys.CsvUpload;
          payload: UploadStatus;
      }
    | {
          type: NewPackFormKeys.DemoUpload;
          payload: UploadStatus;
      }
    | {
          type: NewPackFormKeys.ArtworkUpload;
          payload: UploadStatus;
      }
    | {
          type: NewPackFormKeys.ZipUpload;
          payload: UploadStatus;
      };

function fieldStatusesReducer(
    state: FieldStatusType,
    action: FieldStatusAction,
): FieldStatusType {
    switch (action.type) {
        case NewPackFormKeys.CsvUpload:
            return { ...state, csvUpload: action.payload };
        case NewPackFormKeys.DemoUpload:
            return { ...state, demoUpload: action.payload };
        case NewPackFormKeys.ArtworkUpload:
            return { ...state, artworkUpload: action.payload };
        case NewPackFormKeys.ZipUpload:
            return { ...state, zipUpload: action.payload };
    }
}

// FIELD PROGRESS

function fieldProgressReducer(
    state: FieldProgressType,
    action: FieldProgressAction,
): FieldProgressType {
    switch (action.type) {
        case NewPackFormKeys.CsvUpload:
            return { ...state, csvUpload: action.payload };
        case NewPackFormKeys.DemoUpload:
            return { ...state, demoUpload: action.payload };
        case NewPackFormKeys.ArtworkUpload:
            return { ...state, artworkUpload: action.payload };
        case NewPackFormKeys.ZipUpload:
            return { ...state, zipUpload: action.payload };
    }
}
export type FieldProgressAction =
    | {
          type: NewPackFormKeys.CsvUpload;
          payload: FieldProgress;
      }
    | {
          type: NewPackFormKeys.DemoUpload;
          payload: FieldProgress;
      }
    | {
          type: NewPackFormKeys.ArtworkUpload;
          payload: FieldProgress;
      }
    | {
          type: NewPackFormKeys.ZipUpload;
          payload: FieldProgress;
      };

export const descriptionComponent: Partial<Record<
    NewPackFormVisibleFieldKeys,
    ReactElement<any, any> | null
>> = {
    [NewPackFormKeys.DemoUpload]: (
        <Alert palette="success" mt="md">
            <Alert.Title>
                <Trans>Guidelines for the pack demo</Trans>
            </Alert.Title>
            <Alert.Content>
                <Trans>
                    <ul>
                        <li
                            style={{
                                listStyle: 'disc',
                                marginLeft: '24px',
                            }}
                        >
                            30 seconds or longer
                        </li>
                        <li
                            style={{
                                listStyle: 'disc',
                                marginLeft: '24px',
                            }}
                        >
                            Format: WAV or MP3
                        </li>
                    </ul>
                </Trans>
            </Alert.Content>
        </Alert>
    ),
    [NewPackFormKeys.ArtworkUpload]: (
        <Alert palette="success" mt="md">
            <Alert.Title>
                <Trans>Guidelines for the pack artwork</Trans>
            </Alert.Title>
            <Alert.Content>
                <Trans>
                    <ul>
                        <li
                            style={{
                                listStyle: 'disc',
                                marginLeft: '24px',
                            }}
                        >
                            Perfect square with a minimum resolution of 1000 x
                            1000 pixels
                        </li>
                        <li
                            style={{
                                listStyle: 'disc',
                                marginLeft: '24px',
                            }}
                        >
                            Packs need to include unique artwork that does not
                            infringe on copyright material
                        </li>
                        <li
                            style={{
                                listStyle: 'disc',
                                marginLeft: '24px',
                            }}
                        >
                            Free from offensive or explicit content
                        </li>
                    </ul>
                </Trans>
                <Box mt="lg">
                    <Trans>
                        If needed, you can use this tool{' '}
                        <a
                            href="https://www.iloveimg.com/compress-image/compress-png"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            this tool
                        </a>{' '}
                        to compress your file
                    </Trans>
                </Box>
            </Alert.Content>
        </Alert>
    ),
    [NewPackFormKeys.ZipUpload]: (
        <Alert palette="success" mt="md">
            <Alert.Title>
                <Trans>The ZIP file should contain:</Trans>
            </Alert.Title>
            <Alert.Content>
                <Trans>
                    <ul>
                        <li
                            style={{
                                listStyle: 'disc',
                                marginLeft: '24px',
                            }}
                        >
                            Folder structure
                        </li>
                        <li
                            style={{
                                listStyle: 'disc',
                                marginLeft: '24px',
                            }}
                        >
                            WAV file naming conventions
                        </li>
                        <li
                            style={{
                                listStyle: 'disc',
                                marginLeft: '24px',
                            }}
                        >
                            Do not include full demos or other files along with
                            the samples
                        </li>
                    </ul>
                </Trans>
                <Box mt="lg">
                    <Trans>
                        All these informations are available in the “Before you
                        start“ section on the top of the page.
                    </Trans>
                    <Trans>
                        You can{' '}
                        <a href="./FolderStructure.zip" download>
                            download this zip structure file
                        </a>{' '}
                        to have an example to follow
                    </Trans>
                </Box>
            </Alert.Content>
        </Alert>
    ),
};

const NewPackForm: React.FC<FormikProps<NewPackFormFields>> = ({
    handleSubmit,
    values,
    setValues,
    setFieldValue,
    setFieldError,
    isSubmitting,
    errors,
}) => {
    const [hasError, setHasError] = useState(false);
    const [hasAcceptTerms, setHasAcceptTerms] = useState(null);

    useEffect(() => {
        if (isSubmitting) {
            const hasError =
                Object.keys(errors).length !== 0 &&
                errors.constructor === Object;

            setHasError(hasError || !hasAcceptTerms);

            !hasAcceptTerms && setHasAcceptTerms(false);

            const errorsArray = Object.keys(errors);

            const targetByName = document.getElementsByName(errorsArray[0]);

            const errorTarget = targetByName[0]
                ? targetByName
                : document.querySelectorAll(`[data-testid=${errorsArray[0]}]`);

            errorTarget[0] &&
                errorTarget[0].scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'center',
                });
        }
    }, [errors, hasAcceptTerms, isSubmitting]);

    // REFS
    const genre = useRef<string>(values[NewPackFormKeys.Genre]);

    // REDUCER
    const [fieldStatuses, dispatch] = useReducer(
        fieldStatusesReducer,
        defaultFieldStatuses,
    );
    const [fieldProgress, dispatchProgress] = useReducer(
        fieldProgressReducer,
        defaultFieldProgress,
    );

    // CONTEXT
    const { authToken, canUploadCsv } = useContext(AuthenticationContext);

    // HOOKS
    const { notify } = useNotification();
    const newPackFormSchema = useNewPackFormSchema();

    // EFFECTS
    useEffect(() => {
        // Remove Subgenre selection when changing Genre
        if (genre.current !== values[NewPackFormKeys.Genre]) {
            setValues({ ...values, [NewPackFormKeys.Subgenre]: '' });
            genre.current = values[NewPackFormKeys.Genre];
        }
    }, [setValues, values]);

    // CALLBACKS
    const getUploadStatus = useCallback(
        (fieldKey: string): UploadStatus => {
            if (checkIsUploadField(fieldKey)) {
                return fieldStatuses[fieldKey];
            }

            return 'pending';
        },
        [fieldStatuses],
    );

    const setUploadStatus = useCallback(
        (fieldKey: string, status: UploadStatus): void => {
            if (checkIsUploadField(fieldKey)) {
                dispatch({ type: fieldKey, payload: status });
            }
        },
        [],
    );

    const getUploadProgress = useCallback(
        (fieldKey: string): FieldProgress => {
            if (checkIsUploadField(fieldKey)) {
                return fieldProgress[fieldKey];
            }

            return { uploaded: 0, total: 0 };
        },
        [fieldProgress],
    );

    const setUploadProgress = useCallback(
        (
            fieldKey: string,
            progress: { uploaded: number; total: number },
        ): void => {
            if (checkIsUploadField(fieldKey)) {
                dispatchProgress({ type: fieldKey, payload: progress });
            }
        },
        [],
    );

    const checkIsValid = async (
        fieldKey: UploadKey,
        file: File,
    ): Promise<boolean> => {
        return await newPackFormSchema.fields[fieldKey].isValid(file);
    };

    const doUpload = async (fieldKey: string, file: File): Promise<void> => {
        if (!checkIsUploadField(fieldKey)) {
            return;
        }

        // validation for csv file is already in the schema, needed to be lazy because the field can be hidden
        const isValid =
            fieldKey === 'csvUpload'
                ? true
                : await checkIsValid(fieldKey, file);

        if (!isValid) {
            return;
        }

        setUploadStatus(fieldKey, 'loading');

        const uppyInstance = getUppyInstance(
            UPLOAD_ENPOINTS[fieldKey],
            authToken,
        );

        uppyInstance.addFile({
            name: (file as File).name,
            type: (file as File).type,
            data: file as File,
            source: 'Local',
            isRemote: false,
        });

        uppyInstance.on('upload-error', (file, error, response) => {
            log.error(file.name, EventIds.UppyUploadError, error);
        });

        uppyInstance.on('upload-progress', (file, progress) => {
            const progressObj = {
                uploaded: Math.floor(progress.bytesUploaded / 1000),
                total: Math.ceil(progress.bytesTotal / 1000),
            };
            setUploadProgress(fieldKey, progressObj);
        });

        const handleSuccess = (uploadID: string): void => {
            setFieldValue(`${fieldKey}Id`, uploadID);

            setUploadStatus(fieldKey, 'complete');

            notify({
                variant: 'success',
                content: (
                    <Trans>
                        {`${newPackFieldTitles[fieldKey]} uploaded successfully`}
                    </Trans>
                ),
            });
        };

        const handleFailure = (): void => {
            setFieldValue(`${fieldKey}Id`, '');
            setFieldValue(`${fieldKey}`, '');

            setFieldError(
                fieldKey,
                i18n._(t`Upload failed. Please try again.`),
            );

            notify({
                variant: 'danger',
                content: (
                    <Trans>
                        {`${newPackFieldTitles[fieldKey]} failed to upload.
                        Please try again.`}
                    </Trans>
                ),
            });

            setUploadStatus(fieldKey, 'failed');
        };

        let results;

        try {
            results = await uppyInstance.upload();
        } catch {
            handleFailure();
            return;
        }

        const [successPayload] = results.successful;
        const { uploadId } = (successPayload as any).s3Multipart;

        if (typeof uploadId !== 'undefined') {
            handleSuccess(uploadId as string);

            return;
        }

        handleFailure();
    };

    // BOOLEAN
    const isUploading = Object.values(fieldStatuses).some(
        (value) => value === 'loading',
    );
    const isLoading = isUploading || isSubmitting;

    // Remove optional field
    let newPackFieldLexicon = newPackFieldOrderPart4;
    let newPackFieldOrderFilteredUploaders = newPackFieldOrderPart5;

    if (!canUploadCsv) {
        newPackFieldOrderFilteredUploaders = newPackFieldOrderPart5.filter(
            (val) => {
                // Hide field csv upload when Provider User cannot upload csv
                if (val === 'csvUpload') {
                    return false;
                }
                return true;
            },
        );
    } else {
        newPackFieldLexicon = newPackFieldOrderPart4.filter((val) => {
            // Hide field metadata when Provider User can upload csv
            if (val === 'useSfxLexicon') {
                return false;
            }
            return true;
        });
    }

    return (
        <UploadFieldContext.Provider
            value={{
                getUploadStatus,
                setUploadStatus,
                getUploadProgress,
                setUploadProgress,
                doUpload,
                fieldProgress,
            }}
        >
            <form onSubmit={handleSubmit}>
                <Group direction="vertical" gutter="jb">
                    {newPackFieldOrderPart1.map(
                        (fieldKey: NewPackFormVisibleFieldKeys) => (
                            <Group.Item key={`form-${fieldKey}`}>
                                <InputLayout fieldKey={fieldKey} />
                            </Group.Item>
                        ),
                    )}
                    <Group
                        direction="horizontal"
                        gutter="jb"
                        width="100%"
                        flexDirection={'row'}
                    >
                        {newPackFieldOrderPart2.map(
                            (fieldKey: NewPackFormVisibleFieldKeys) => (
                                <Group.Item
                                    key={`form-${fieldKey}`}
                                    width="100%"
                                >
                                    <InputLayout fieldKey={fieldKey} />
                                </Group.Item>
                            ),
                        )}
                    </Group>
                    <Group
                        direction="horizontal"
                        gutter="jb"
                        width="100%"
                        flexDirection={'row'}
                        mt="jb"
                        mb="jb"
                    >
                        {newPackFieldOrderPart3.map(
                            (fieldKey: NewPackFormVisibleFieldKeys) => (
                                <Group.Item
                                    key={`form-${fieldKey}`}
                                    width="50%"
                                >
                                    <InputLayout fieldKey={fieldKey} />
                                </Group.Item>
                            ),
                        )}
                    </Group>
                    {newPackFieldLexicon.map(
                        (fieldKey: NewPackFormVisibleFieldKeys) => (
                            <Group.Item key={`form-${fieldKey}`}>
                                <InputLayout
                                    fieldKey={fieldKey}
                                    isDescriptionOnTop={true}
                                />
                            </Group.Item>
                        ),
                    )}
                    <Divider pb="jb" />
                    <Heading as="h2" size="xl" textAlign="left" mb="lg">
                        <Trans>Pack files</Trans>
                    </Heading>
                    {newPackFieldOrderFilteredUploaders.map(
                        (fieldKey: NewPackFormVisibleFieldKeys) => (
                            <Group.Item key={`form-${fieldKey}`}>
                                <InputLayout
                                    fieldKey={fieldKey}
                                    isDescriptionOnTop={true}
                                />
                            </Group.Item>
                        ),
                    )}
                    <Divider pb="jb" />
                    <Heading as="h2" size="xl" textAlign="left" mb="lg">
                        <Trans>Accept conditions</Trans>
                    </Heading>
                    <Group.Item>
                        <Flex flexDirection="column">
                            <Checkbox
                                label={
                                    <Trans>
                                        I confirm that all elements (audio,
                                        image, and text) as well as the
                                        submitted information comply with the
                                        instructions provided by LANDR. I
                                        understand that my pack may be removed
                                        or taken offline if this is not the
                                        case.
                                    </Trans>
                                }
                                isChecked={hasAcceptTerms}
                                onChange={(): void => {
                                    hasAcceptTerms
                                        ? setHasAcceptTerms(false)
                                        : setHasAcceptTerms(true);
                                }}
                                isInvalid={hasAcceptTerms === false}
                            />
                        </Flex>
                        <Flex justifyContent="flex-end" mt="lg" mb="200px">
                            <Button
                                type="submit"
                                isLoading={isLoading}
                                isRounded={true}
                            >
                                <Trans>Submit the pack</Trans>
                            </Button>
                        </Flex>
                    </Group.Item>
                </Group>
            </form>
            {hasError && (
                <UploadMessageWrapper>
                    <Message variant="danger" kind="default">
                        <Text>
                            <Trans>Please check required fields</Trans>
                        </Text>
                    </Message>
                </UploadMessageWrapper>
            )}
            {isUploading && (
                <UploadMessageWrapper>
                    <Message variant="warning" kind="default">
                        <Text>
                            <Trans>
                                Please do not go back or close your browser
                                until the upload is finished
                            </Trans>
                        </Text>
                    </Message>
                </UploadMessageWrapper>
            )}
        </UploadFieldContext.Provider>
    );
};

export default NewPackForm;
